// Organization Context - manages current organization and project
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { organizationsService } from '@/services/collections/organizations';
import type { Organization, OrganizationMember, Project } from '@/types';

interface OrganizationContextValue {
  // Current organization
  organization: Organization | null;
  organizations: Organization[];
  loadingOrg: boolean;

  // Current project
  project: Project | null;
  projects: Project[];
  loadingProjects: boolean;

  // Current user's membership
  member: OrganizationMember | null;

  // Actions
  setCurrentOrganization: (orgId: string) => Promise<void>;
  setCurrentProject: (projectId: string) => Promise<void>;
  refreshOrganization: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  reloadOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user, profile } = useAuth();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrg, setLoadingOrg] = useState(true);

  const [project, setProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [member, setMember] = useState<OrganizationMember | null>(null);

  // Load all organizations for the user
  const loadOrganizations = useCallback(async () => {
    setLoadingOrg(true);

    // Check if we should try loading from localStorage (after onboarding)
    const onboardingCompleted = localStorage.getItem('onboardingCompleted');
    const savedOrgId = localStorage.getItem('currentOrgId');

    // Build list of org IDs to load
    let orgIdsToLoad = profile?.organizationIds || [];

    // If onboarding was just completed and profile doesn't have the org yet,
    // try loading the saved org directly
    if (onboardingCompleted && savedOrgId && !orgIdsToLoad.includes(savedOrgId)) {
      orgIdsToLoad = [savedOrgId, ...orgIdsToLoad];
    }

    if (orgIdsToLoad.length === 0) {
      setOrganizations([]);
      setOrganization(null);
      setLoadingOrg(false);
      return;
    }

    try {
      const orgs: Organization[] = [];
      for (const orgId of orgIdsToLoad) {
        const org = await organizationsService.getById(orgId);
        if (org) orgs.push(org);
      }
      setOrganizations(orgs);

      // Clear onboarding flag once we successfully loaded organizations
      if (orgs.length > 0 && onboardingCompleted) {
        localStorage.removeItem('onboardingCompleted');
      }

      // Set first org as current if none selected
      if (!organization && orgs.length > 0) {
        await setCurrentOrganization(orgs[0].id);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoadingOrg(false);
    }
  }, [profile?.organizationIds]);

  // Set current organization
  const setCurrentOrganization = useCallback(async (orgId: string) => {
    setLoadingOrg(true);
    try {
      const org = await organizationsService.getById(orgId);
      if (!org) {
        console.error('Organization not found:', orgId);
        return;
      }

      // Verify user is a member of this organization before granting access
      if (user) {
        const memberData = await organizationsService.getMember(orgId, user.uid);
        if (!memberData) {
          console.error('User is not a member of organization:', orgId);
          localStorage.removeItem('currentOrgId');
          return;
        }
        setMember(memberData);
      }

      setOrganization(org);

      // Store in localStorage for persistence
      localStorage.setItem('currentOrgId', orgId);

      // Load projects for this org
      await loadProjects(orgId);
    } catch (error) {
      console.error('Error setting organization:', error);
    } finally {
      setLoadingOrg(false);
    }
  }, [user]);

  // Load projects for current organization
  const loadProjects = async (orgId: string) => {
    setLoadingProjects(true);
    try {
      const projectList = await organizationsService.getProjects(orgId);
      setProjects(projectList);

      // Set first project as current if none selected
      if (!project && projectList.length > 0) {
        await setCurrentProjectInternal(orgId, projectList[0].id);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Set current project
  const setCurrentProjectInternal = async (orgId: string, projectId: string) => {
    try {
      const proj = await organizationsService.getProject(orgId, projectId);
      setProject(proj);

      // Store in localStorage
      if (proj) {
        localStorage.setItem('currentProjectId', projectId);
      }
    } catch (error) {
      console.error('Error setting project:', error);
    }
  };

  const setCurrentProject = useCallback(async (projectId: string) => {
    if (!organization) return;
    await setCurrentProjectInternal(organization.id, projectId);
  }, [organization]);

  // Refresh current organization
  const refreshOrganization = useCallback(async () => {
    if (organization) {
      await setCurrentOrganization(organization.id);
    }
  }, [organization, setCurrentOrganization]);

  // Refresh projects
  const refreshProjects = useCallback(async () => {
    if (organization) {
      await loadProjects(organization.id);
    }
  }, [organization]);

  // Force reload organizations (used after onboarding)
  const reloadOrganizations = useCallback(async () => {
    await loadOrganizations();
  }, [loadOrganizations]);

  // Load organizations when profile changes
  useEffect(() => {
    if (profile) {
      loadOrganizations();
    } else {
      setOrganizations([]);
      setOrganization(null);
      setProjects([]);
      setProject(null);
      setMember(null);
      setLoadingOrg(false);
    }
  }, [profile, loadOrganizations]);

  // Try to restore from localStorage
  useEffect(() => {
    const savedOrgId = localStorage.getItem('currentOrgId');
    if (savedOrgId && profile?.organizationIds?.includes(savedOrgId) && !organization) {
      setCurrentOrganization(savedOrgId);
    }
  }, [profile, organization, setCurrentOrganization]);

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        organizations,
        loadingOrg,
        project,
        projects,
        loadingProjects,
        member,
        setCurrentOrganization,
        setCurrentProject,
        refreshOrganization,
        refreshProjects,
        reloadOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};
